import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { GET_MY_STORE, UPDATE_STORE, type ManageMyStoreData } from '../../graphql/management'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

// Edit the seller's own store: name, description, and the snake_case keys in the
// freeform `settings` JSON (which is passed straight through — the JSON scalar
// serialises the object; no stringify like the iOS Apollo workaround). Mirrors
// iOS StoreSettingsView, focused on the core toggles + currency for v1.

const CURRENCIES = ['GBP', 'USD', 'EUR']

function asBool(v: unknown): boolean { return v === true || v === 'true' }
function asStr(v: unknown, fallback = ''): string { return typeof v === 'string' ? v : fallback }

export default function StoreSettings() {
  const navigate = useNavigate()
  const fb = useFormFeedback()
  const { data, loading } = useQuery<ManageMyStoreData>(GET_MY_STORE, { fetchPolicy: 'cache-and-network' })
  const [save, { loading: saving }] = useMutation(UPDATE_STORE)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [settings, setSettings] = useState<Record<string, unknown>>({})

  // Seed the form once the store loads.
  const store = data?.myStore
  useEffect(() => {
    if (!store) return
    setName(store.name)
    setDescription(store.description ?? '')
    setSettings(store.settings ?? {})
  }, [store])

  if (loading && !store) return <div className="muted">Loading settings…</div>
  if (!store) return <div className="empty-state"><h2>No store</h2></div>

  function setKey(key: string, value: unknown) {
    setSettings((s) => ({ ...s, [key]: value }))
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    fb.reset()
    try {
      const { data: res } = await save({
        variables: { id: store!.id, name, description, settings },
      })
      const errs = res?.updateStore?.userErrors as UserError[] | undefined
      if (!fb.fromUserErrors(errs)) return
      fb.setSuccess('Saved.')
    } catch (err) {
      fb.setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>Store settings</h1>
        <button className="btn-link" onClick={() => navigate('/manage/store')}>← Back</button>
      </header>

      <form className="create-form" onSubmit={onSave}>
        <label><span>Store name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label><span>Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </label>
        <label><span>Currency</span>
          <select value={asStr(settings.currency, 'GBP')} onChange={(e) => setKey('currency', e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <fieldset className="toggle-group">
          <label className="toggle">
            <input type="checkbox" checked={asBool(settings.shipping_enabled)} onChange={(e) => setKey('shipping_enabled', e.target.checked)} />
            <span>Shipping enabled</span>
          </label>
          <label className="toggle">
            <input type="checkbox" checked={asBool(settings.tax_enabled)} onChange={(e) => setKey('tax_enabled', e.target.checked)} />
            <span>Tax enabled</span>
          </label>
          <label className="toggle">
            <input type="checkbox" checked={asBool(settings.livestream_enabled)} onChange={(e) => setKey('livestream_enabled', e.target.checked)} />
            <span>Livestreaming enabled</span>
          </label>
        </fieldset>

        <Feedback error={fb.error} success={fb.success} />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        </div>
      </form>
    </div>
  )
}
